// Podcast publish pipeline — usePodcastAudio: the in-app native-audio player hook.
//
// Near-clone of `useBriefAudio.ts` (AUD-4), retargeted at `podcast_publish_episodes` (the
// publish pipeline's own table — DISTINCT from the m27 `podcast_episodes` YouTube-sync table;
// see docs/build-records/sql/m28_podcast_publish.sql's reconciliation note) instead of
// `research_brief_episodes`. The whole signed-URL → native predownload → <audio> →
// mediaSession → Android foreground-service chain is already solved there; we reuse it
// verbatim EXCEPT for one simplification: the podcast enclosure bucket (`podcast-media`) is
// PUBLIC (see m28_podcast_publish.sql — bucket policy `podcast_media_public_read`), so
// playback uses the plain public object URL directly. No `createSignedUrl` round-trip, no
// 1-hour expiry to babysit, and it still works for an unauthenticated crawler-style fetch
// (though this hook itself always runs authenticated, behind ProtectedRoute). The native
// predownload-for-offline path is otherwise unchanged.
//
// `podcast_publish_episodes` (m28) is not in the generated Supabase types yet, so — exactly
// like useBriefAudio — the table literal is cast through `any` at the query call site.
// Regenerate types.ts once the migration lands and switch back to the typed `.from()` call then.
//
// Used by MobilePodcast.tsx's <NativeAudioPlayer> only for episodes where the joined
// `mobile_podcast_episodes()` RPC row has an `audio_path` (i.e. the m27-synced episode was
// ALSO published through this pipeline) — the caller selects into this hook via
// `selectEpisode(publish_episode_id)`. Fallback: when an episode has no playable audio (no
// `audio_path`, or predownload/playback fails) but DOES have a `youtube_video_id`, the caller
// renders a YouTube <iframe> instead. This hook only owns the native-audio path; it surfaces
// `useYoutubeFallback` so the screen knows when to fall back.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { removeCached } from '@/lib/offline/persistentCache';
import { isNative } from '@/lib/native/platform';

// ---------------------------------------------------------------------------
// Shapes (podcast_publish_episodes — stale generated types → cast at the call site)
// ---------------------------------------------------------------------------

export interface PodcastEpisode {
  id: string;
  title: string;
  description: string | null;
  episode_number: number | null;
  season_number: number | null;
  audio_path: string | null;
  duration_sec: number | null;
  youtube_video_id: string | null;
  published_at: string | null;
  created_at: string | null;
}

const STORAGE_BUCKET = 'podcast-media';
// Distinct from MobilePodcast.tsx's own cache ('mobile.podcast_episodes' / ['mobile-podcast-
// episodes']) — this hook fetches a DIFFERENT table (podcast_publish_episodes, not the
// mobile_podcast_episodes() RPC's joined view), so it needs its own cache/query key to avoid
// two different queryFns writing through the same offline-cache slot.
const CACHE_KEY = 'mobile.podcast_publish_episodes';
const QUERY_KEY = ['mobile-podcast-publish-episodes'] as const;
const SUPABASE_URL = 'https://ewyfhrjexfrmcjhqzsmm.supabase.co';

// Where the pre-downloaded episode audio lives on native (app-sandboxed Cache dir). Keyed by
// episode id (unlike the single-file brief cache) since a listener may switch between several
// published episodes in one session and we don't want each switch to evict the last download.
const PODCAST_DIR = 'cbam-podcast';
function podcastFilePath(episodeId: string): string {
  // `.m4a` extension is nominal only (see useBriefAudio's identical note) — the webview sniffs
  // the actual container from content, not the filename.
  return `${PODCAST_DIR}/${episodeId}.m4a`;
}

// Build the public (unauthenticated) object URL for an enclosure. Mirrors the URL shape the
// `podcast-feed` edge function emits in its RSS <enclosure>, so app playback and the public feed
// resolve to the exact same bytes.
export function publicAudioUrl(audioPath: string): string {
  return `${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET}/${audioPath}`;
}

// Map a raw row → typed PodcastEpisode.
function mapEpisode(r: Record<string, unknown>): PodcastEpisode {
  return {
    id: String(r.id),
    title: (r.title as string | null) ?? 'Untitled episode',
    description: (r.description as string | null) ?? null,
    episode_number: typeof r.episode_number === 'number' ? r.episode_number : null,
    season_number: typeof r.season_number === 'number' ? r.season_number : null,
    audio_path: (r.audio_path as string | null) ?? null,
    duration_sec: typeof r.duration_sec === 'number' ? r.duration_sec : null,
    youtube_video_id: (r.youtube_video_id as string | null) ?? null,
    published_at: (r.published_at as string | null) ?? null,
    created_at: (r.created_at as string | null) ?? null,
  };
}

// Throw-on-error fetcher. useCachedQuery requires the queryFn to THROW so the cached placeholder
// is served offline. Returns every episode that has SOME playable target (audio or YouTube),
// newest first. Returns [] when there are no published episodes yet (a valid, non-error state).
async function fetchPodcastEpisodes(): Promise<PodcastEpisode[]> {
  const { data, error } = await (supabase as any)
    .from('podcast_publish_episodes')
    .select(
      'id, title, description, episode_number, season_number, audio_path, duration_sec, youtube_video_id, published_at, created_at',
    )
    .or('audio_path.not.is.null,youtube_video_id.not.is.null')
    .order('published_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) throw error;

  const rows = (Array.isArray(data) ? data : []) as Record<string, unknown>[];
  return rows.map(mapEpisode);
}

// ---------------------------------------------------------------------------
// Native pre-download (best-effort, web no-op). Never throws into the hook.
// ---------------------------------------------------------------------------

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

// Download the public enclosure bytes and stash them in the app-sandboxed Cache dir so the
// episode plays offline. Returns the on-device file URI (webview-playable) or null when not
// native / on any failure (caller falls back to the public URL). Mirrors useBriefAudio's
// predownloadNative idiom exactly (dynamic import behind isNative(), try/catch).
async function predownloadNative(episodeId: string, url: string): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const b64 = await blobToBase64(blob);

    await Filesystem.mkdir({
      path: PODCAST_DIR,
      directory: Directory.Cache,
      recursive: true,
    }).catch(() => {
      /* already exists */
    });
    const path = podcastFilePath(episodeId);
    await Filesystem.writeFile({ path, data: b64, directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
    try {
      const { Capacitor } = await import('@capacitor/core');
      return Capacitor.convertFileSrc(uri);
    } catch {
      return uri;
    }
  } catch {
    /* plugin/IO unavailable: stay fail-open — playback uses the public URL */
    return null;
  }
}

// Probe for an already-downloaded episode file (native only) without re-fetching. Used OFFLINE.
async function probeDownloadedNative(episodeId: string): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const path = podcastFilePath(episodeId);
    await Filesystem.stat({ path, directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
    try {
      const { Capacitor } = await import('@capacitor/core');
      return Capacitor.convertFileSrc(uri);
    } catch {
      return uri;
    }
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Native foreground-service bridge (Android background / screen-locked playback). Reuses the
// SAME BriefAudioService plugin as the audio brief — it is a generic "keep the WebView's audio
// alive" foreground service, not brief-specific, so there is no need for a second native plugin.
// All calls are gated behind isNative(), dynamic-import the plugin, and swallow every error.
// ---------------------------------------------------------------------------

interface BriefAudioServicePlugin {
  start(options: { title?: string }): Promise<void>;
  stop(): Promise<void>;
  update(options: { title?: string }): Promise<void>;
}

async function getAudioServicePlugin(): Promise<BriefAudioServicePlugin | null> {
  if (!isNative()) return null;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    return registerPlugin<BriefAudioServicePlugin>('BriefAudioService');
  } catch {
    return null;
  }
}

async function startNativeAudioService(title: string): Promise<void> {
  try {
    const plugin = await getAudioServicePlugin();
    if (!plugin) return;
    await plugin.start({ title });
  } catch {
    /* plugin/native unavailable — playback still works via the WebView while foregrounded */
  }
}

async function stopNativeAudioService(): Promise<void> {
  try {
    const plugin = await getAudioServicePlugin();
    if (!plugin) return;
    await plugin.stop();
  } catch {
    /* best-effort */
  }
}

/**
 * Remove ALL pre-downloaded podcast audio + the cached episode list. Best-effort, never throws.
 * Exported so the sign-out / wipe path can purge confidential local state on logout — wire this
 * alongside `purgeBriefAudio()` at the same call site (see useBriefAudio.ts's WIRING note).
 */
export async function purgePodcastAudio(): Promise<void> {
  if (isNative()) {
    try {
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      await Filesystem.rmdir({
        path: PODCAST_DIR,
        directory: Directory.Cache,
        recursive: true,
      }).catch(() => {});
    } catch {
      /* plugin unavailable / already gone */
    }
  }
  try {
    await removeCached(CACHE_KEY);
  } catch {
    /* best-effort */
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UsePodcastAudio {
  episodes: PodcastEpisode[];
  episode: PodcastEpisode | null;
  selectEpisode: (id: string) => void;
  /** True when the current episode has no usable native audio and a YouTube id exists instead —
   * the screen should render the iframe fallback. */
  useYoutubeFallback: boolean;
  playing: boolean;
  currentTime: number;
  duration: number;
  rate: number;
  isFromCache: boolean;
  cachedAt: string | null;
  loading: boolean;
  downloaded: boolean;
  error: string | null;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (sec: number) => void;
  setRate: (r: number) => void;
}

export function usePodcastAudio(): UsePodcastAudio {
  const query = useCachedQuery<PodcastEpisode[]>({
    queryKey: QUERY_KEY as unknown as readonly unknown[],
    cacheKey: CACHE_KEY,
    queryFn: fetchPodcastEpisodes,
  });

  const episodes = useMemo<PodcastEpisode[]>(() => {
    const d = query.data as unknown;
    if (Array.isArray(d)) return d as PodcastEpisode[];
    return [];
  }, [query.data]);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Default to the first (newest) episode once the list loads; keep the current selection if it
  // still exists in a refreshed list.
  useEffect(() => {
    if (episodes.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => {
      if (prev && episodes.some((e) => e.id === prev)) return prev;
      return episodes[0].id;
    });
  }, [episodes]);

  const selectEpisode = useCallback((id: string) => setSelectedId(id), []);

  const episode = useMemo<PodcastEpisode | null>(() => {
    if (episodes.length === 0) return null;
    return episodes.find((e) => e.id === selectedId) ?? episodes[0];
  }, [episodes, selectedId]);

  // "Playable" here means we attempted native-audio resolution; whether it ACTUALLY has audio is
  // tracked via playableAudioUrl below (null when there's no audio_path or nothing resolved).
  const audioRef = useRef<HTMLAudioElement | null>(null);
  if (audioRef.current == null && typeof Audio !== 'undefined') {
    audioRef.current = new Audio();
  }

  const [playableUrl, setPlayableUrl] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [resolveFailed, setResolveFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRateState] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // ---- Resolve playback source whenever the episode changes -------------------------------------
  // Prefer-order (per spec §5): local predownload → public object URL → (caller falls back to
  // YouTube iframe when this resolves to nothing playable).
  useEffect(() => {
    let cancelled = false;
    setPlayableUrl(null);
    setDownloaded(false);
    setResolveFailed(false);
    setError(null);

    const ep = episode;
    const path = ep?.audio_path;
    if (!ep || !path) {
      // No native audio for this episode at all — the caller should render the YouTube fallback
      // immediately if a video id exists.
      setResolveFailed(true);
      return;
    }

    const url = publicAudioUrl(path);

    (async () => {
      // Try the on-disk copy first (works fully offline, no network round-trip at all).
      const cached = await probeDownloadedNative(ep.id);
      if (cancelled) return;
      if (cached) {
        setDownloaded(true);
        setPlayableUrl(cached);
        const el = audioRef.current;
        if (el && el.src !== cached) el.src = cached;
        return;
      }

      // Nothing on disk yet — use the public URL directly (no signing needed, public bucket).
      setPlayableUrl(url);
      const el = audioRef.current;
      if (el && el.src !== url) el.src = url;

      // Best-effort background predownload for next time / offline continuity.
      const localSrc = await predownloadNative(ep.id, url);
      if (cancelled) return;
      if (localSrc) {
        setDownloaded(true);
        const el2 = audioRef.current;
        if (el2) el2.src = localSrc;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [episode?.id, episode?.audio_path]);

  // True when there's genuinely nothing to play natively — no audio_path at all — and a YouTube
  // id exists. (A transient network failure to reach the CDN falls back to the <audio> `error`
  // event below rather than this flag, since the URL itself is still valid.)
  const useYoutubeFallback = resolveFailed && !!episode?.youtube_video_id;

  // ---- Audio element event wiring ----------------------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onTime = () => setCurrentTime(el.currentTime || 0);
    const onDuration = () => {
      const d = el.duration;
      setDuration(Number.isFinite(d) ? d : episode?.duration_sec ?? 0);
    };
    const serviceTitle = episode?.title ? `Podcast — ${episode.title}` : 'CBAM podcast';

    const onPlay = () => {
      setPlaying(true);
      void startNativeAudioService(serviceTitle);
    };
    const onPause = () => {
      setPlaying(false);
      void stopNativeAudioService();
    };
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
      void stopNativeAudioService();
    };
    const onError = () => setError('We couldn’t play this episode. Try again when you’re back online.');

    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onDuration);
    el.addEventListener('durationchange', onDuration);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);

    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onDuration);
      el.removeEventListener('durationchange', onDuration);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
    };
  }, [episode?.duration_sec, episode?.title]);

  // Seed duration from row metadata before the element loads.
  useEffect(() => {
    if (episode?.duration_sec && duration === 0) setDuration(episode.duration_sec);
  }, [episode?.duration_sec, duration]);

  // Stop + reset when the episode goes away (e.g. cache cleared).
  useEffect(() => {
    if (!episode) {
      const el = audioRef.current;
      if (el) {
        try {
          el.pause();
          el.removeAttribute('src');
          el.load();
        } catch {
          /* ignore */
        }
      }
      setPlaying(false);
      setCurrentTime(0);
    }
  }, [episode]);

  // ---- Controls ----------------------------------------------------------------------------------
  const play = useCallback(() => {
    const el = audioRef.current;
    if (!el || !el.src) return;
    void el.play().catch(() => {
      setError('We couldn’t play this episode. Try again when you’re back online.');
    });
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) play();
    else el.pause();
  }, [play]);

  const seek = useCallback((sec: number) => {
    const el = audioRef.current;
    if (!el) return;
    const clamped = Math.max(0, Number.isFinite(sec) ? sec : 0);
    try {
      el.currentTime = clamped;
      setCurrentTime(clamped);
    } catch {
      /* seeking before metadata is loaded — ignore */
    }
  }, []);

  const setRate = useCallback((r: number) => {
    const el = audioRef.current;
    setRateState(r);
    if (el) el.playbackRate = r;
  }, []);

  useEffect(() => {
    const el = audioRef.current;
    if (el) el.playbackRate = rate;
  }, [rate, playableUrl, downloaded]);

  // ---- Pause on unmount ----------------------------------------------------------------------------
  useEffect(() => {
    return () => {
      try {
        audioRef.current?.pause();
      } catch {
        /* ignore */
      }
      void stopNativeAudioService();
    };
  }, []);

  // ---- navigator.mediaSession (Android lock-screen transport while the process is alive) ---------
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    if (!episode) return;

    try {
      if (typeof MediaMetadata !== 'undefined') {
        ms.metadata = new MediaMetadata({
          title: episode.title,
          artist: 'CBAM podcast',
          album: 'CBAM podcast',
        });
      }
    } catch {
      /* metadata best-effort */
    }

    const setHandler = (
      action: MediaSessionAction,
      handler: MediaSessionActionHandler | null,
    ) => {
      try {
        ms.setActionHandler(action, handler);
      } catch {
        /* unsupported action on this platform — ignore */
      }
    };

    setHandler('play', () => play());
    setHandler('pause', () => pause());
    setHandler('seekbackward', (d) => seek((audioRef.current?.currentTime ?? 0) - (d.seekOffset ?? 15)));
    setHandler('seekforward', (d) => seek((audioRef.current?.currentTime ?? 0) + (d.seekOffset ?? 15)));

    return () => {
      for (const a of ['play', 'pause', 'seekbackward', 'seekforward'] as MediaSessionAction[]) {
        setHandler(a, null);
      }
    };
  }, [episode, play, pause, seek]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
    } catch {
      /* ignore */
    }
  }, [playing]);

  return {
    episodes,
    episode,
    selectEpisode,
    useYoutubeFallback,
    playing,
    currentTime,
    duration,
    rate,
    isFromCache: query.isFromCache,
    cachedAt: query.cachedAt,
    loading: query.isLoading,
    downloaded,
    error,
    play,
    pause,
    toggle,
    seek,
    setRate,
  };
}
